import React from 'react';
import { FiSave, FiCheck, FiAlertCircle, FiLoader, FiClock } from 'react-icons/fi';

interface AutoSaveStatus {
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastSaved?: Date;
  error?: string;
  isManual?: boolean;
}

interface EditorTopBarProps {
  autoSaveStatus: AutoSaveStatus;
  hasDraft: boolean;
  onManualSave?: () => void;
  onShowDraftRecovery?: () => void;
  programTitle?: string;
  isEditing?: boolean;
}

/**
 * Top bar for the program editor with auto-save status and quick actions
 * Provides better UX with prominent save button and status indicators
 */
const EditorTopBar: React.FC<EditorTopBarProps> = ({
  autoSaveStatus,
  hasDraft,
  onManualSave,
  onShowDraftRecovery,
  programTitle,
  isEditing = false
}) => {
  const getStatusConfig = () => {
    switch (autoSaveStatus.status) {
      case 'saving':
        return {
          icon: FiLoader,
          text: 'Saving...',
          className: 'text-blue-600',
          iconClassName: 'animate-spin'
        };
      case 'saved':
        return {
          icon: FiCheck,
          text: autoSaveStatus.isManual 
            ? 'Manually saved'
            : autoSaveStatus.lastSaved 
              ? `Auto-saves (every 5s)`
              : 'Saved',
          className: 'text-green-600',
          iconClassName: ''
        };
      case 'error':
        return {
          icon: FiAlertCircle,
          text: autoSaveStatus.error || 'Save failed',
          className: 'text-red-600',
          iconClassName: ''
        };
      default:
        return {
          icon: FiClock,
          text: 'Auto-save every 5s',
          className: 'text-gray-500',
          iconClassName: ''
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div className="w-full" role="banner">
      <div className="flex items-center justify-between ps-3 pe-0 py-2">
        {/* Left side - Title and status */}
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Program' : 'Create Program'}
            </h1>
            {programTitle && (
              <p className="text-sm text-gray-600 truncate max-w-xs" aria-label={`Current program title: ${programTitle}`}>
                {programTitle}
              </p>
            )}
          </div>

          {/* Auto-save status */}
          <div
            className={`flex items-center gap-2 text-sm ${config.className}`}
            role="status"
            aria-live="polite"
            aria-label={`Auto-save status: ${config.text}`}
          >
            <Icon className={`h-4 w-4 ${config.iconClassName}`} aria-hidden="true" />
            <span>{config.text}</span>
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-3 me-0 sm:me-2 md:me-4 lg:me-6">
          {/* Draft recovery button */}
          {hasDraft && (
            <button
              onClick={onShowDraftRecovery}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <FiAlertCircle className="h-4 w-4" />
              Draft Available
            </button>
          )}

          {/* Manual save button */}
          {onManualSave && (
            <button
              onClick={onManualSave}
              disabled={autoSaveStatus.status === 'saving'}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <FiSave className="h-4 w-4" />
              Save Draft
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditorTopBar;
